import { useMutation, useQuery } from '@tanstack/react-query'
import { get, post } from '../fetch'

export type OverspeedIncident = {
	from: string
	to: string
	durationSeconds: number
	maxSpeed: number
	maxAt: string
	lat: number | null
	lng: number | null
	samples: number
}

export type OverspeedStatus = 'ok' | 'no_device' | 'not_configured' | 'not_found' | 'invalid_period'

export type OverspeedResult = {
	status: OverspeedStatus
	limit: number
	category?: string | null
	deviceId?: string
	custom?: boolean
	period?: { from: string; to: string }
	incidentCount?: number
	maxSpeed?: number | null
	sampleCount?: number
	incidents?: Array<OverspeedIncident>
	message?: string
}

export type VehicleLocationResult = {
	status: 'ok' | 'no_device' | 'not_configured' | 'not_found' | 'no_position'
	deviceId?: string
	lat?: number
	lng?: number
	time?: string | null
	speed?: number | null
	name?: string | null
}

export type OverspeedPeriod = { from: string; to: string }

export type StoredOverspeedIncident = {
	started_at: string
	ended_at: string
	duration_seconds: number
	max_speed: number
	speed_limit: number | null
	lat: number | null
	lng: number | null
}

/** Uložená historie překročení rychlosti k rezervaci. */
export function useReservationOverspeedHistory(id: string) {
	return useQuery({
		queryKey: ['api/reservation/overspeed/history', id],
		queryFn: () =>
			get<Array<StoredOverspeedIncident>>(`/api/0/reservation/${id}/overspeed/history`),
		staleTime: 60 * 1000,
		refetchOnWindowFocus: false,
	})
}

// 'warning' zůstává jen kvůli zobrazení starších (dřív vyřízených) případů —
// nově se dá založit pouze 'fine' nebo 'dismissed', viz ResolveOverspeedInput.
export type OverspeedResolution = 'fine' | 'warning' | 'dismissed'

export type FlaggedOverspeed = {
	reservation_id: string
	vehicle_id: string
	client_id: string
	date_from: string
	date_to: string
	status: string
	case_status: 'open' | 'resolved'
	resolution: OverspeedResolution | null
	fine_amount: number | null
	note: string | null
	resolved_at: string | null
	// Vyplněné jen u dočasného zamítnutí („na týden") — do kdy platí; po
	// uplynutí se případ při dalším incidentu sám znovu otevře. NULL = trvale
	// zamítnuto (nebo jiný způsob vyřízení).
	snoozed_until: string | null
	incident_count: number
	max_speed: number
	first_at: string
	last_at: string
	total_seconds: number
	is_short_term: boolean
}

export type FlaggedFilter = OverspeedPeriod & { status?: 'open' | 'resolved' }

/** Seznam rezervací s uloženými překročeními (dashboard). */
export function useOverspeedFlagged(filter?: Partial<FlaggedFilter>) {
	return useQuery({
		queryKey: [
			'api/overspeed/flagged',
			filter?.from ?? null,
			filter?.to ?? null,
			filter?.status ?? null,
		],
		queryFn: () => {
			const params = new URLSearchParams()
			if (filter?.from) params.set('from', filter.from)
			if (filter?.to) params.set('to', filter.to)
			if (filter?.status) params.set('status', filter.status)
			return get<Array<FlaggedOverspeed>>(`/api/0/overspeed/flagged`, params)
		},
		staleTime: 60 * 1000,
		refetchOnWindowFocus: false,
	})
}

/** Způsoby vyřízení, které lze nově založit (viz OverspeedResolution pro čtení starších dat). */
export type ResolveOverspeedInput = {
	reservationId: string
	resolution: 'fine' | 'dismissed'
	amount?: number
	note?: string
	/** Jen u 'dismissed': počet dní do znovuotevření (např. 7 = „na týden"). Bez vyplnění = trvale. */
	snoozeDays?: number
}

/** Vyřídí případ překročení (pokuta / zamítnuto — trvale nebo na týden). */
export function useResolveOverspeed() {
	return useMutation({
		mutationFn: ({
			reservationId,
			resolution,
			amount,
			note,
			snoozeDays,
		}: ResolveOverspeedInput) =>
			post(`/api/0/overspeed/${reservationId}/resolve`, {
				resolution,
				amount,
				note,
				snoozeDays,
			}),
		meta: {
			invalidateQueries: [['api/overspeed/flagged']],
		},
	})
}

/** Znovu otevře vyřízený případ. */
export function useReopenOverspeed() {
	return useMutation({
		mutationFn: (reservationId: string) => post(`/api/0/overspeed/${reservationId}/reopen`),
		meta: {
			invalidateQueries: [['api/overspeed/flagged']],
		},
	})
}

/**
 * Mapa reservation_id → souhrn překročení (pro odznaky v kalendáři – „grafika").
 * Jen nevyřízené případy — jakmile se případ pokutuje nebo zamítne (trvale i
 * na týden), odznak z kalendáře zmizí. Vrací se zpět jen pokud se případ
 * u dočasného zamítnutí po týdnu sám znovu otevře (viz ensureCase na serveru).
 */
export function useOverspeedFlaggedMap() {
	const query = useOverspeedFlagged({ status: 'open' })
	const map: Record<string, FlaggedOverspeed> = {}
	for (const row of query.data ?? []) map[String(row.reservation_id)] = row
	return map
}

/**
 * Lazy query — only runs once `enabled` is true (i.e. the user opened the modal).
 * When `period` is provided it overrides the rental period with a custom one.
 */
export function useReservationOverspeed(
	id: string,
	enabled: boolean,
	period?: OverspeedPeriod | null,
) {
	return useQuery({
		queryKey: ['api/reservation/overspeed', id, period?.from ?? null, period?.to ?? null],
		queryFn: () => {
			const params = new URLSearchParams()
			if (period?.from) params.set('from', period.from)
			if (period?.to) params.set('to', period.to)
			return get<OverspeedResult>(`/api/0/reservation/${id}/overspeed`, params)
		},
		enabled,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		retry: false,
	})
}
